import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, animate, motion, useAnimationFrame, useInView, useMotionValue, useReducedMotion, useScroll, useSpring, useTransform, useVelocity, wrap } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Building2,
  CircuitBoard,
  ClipboardCheck,
  Factory,
  FileCheck2,
  FileText,
  House,
  MapPin,
  PhoneCall,
  PlugZap,
  Route,
  Send,
  ShieldCheck,
  Users,
  Wrench,
} from "lucide-react";
import { COMPANY, FACTS, HERO, HERO_TILE, IMG, MARKETS, MARKET_ORDER, PROJECTS_INTRO, PULSE, sized } from "@/lib/content";
import { MARKET } from "@/lib/format";
import { Btn, Img, TextLink } from "@/components/public/ui";
import { Skeleton } from "@/components/ui/skeleton";
import { EASE, Reveal } from "@/components/public/Reveal";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------
   Front page building blocks.
   Type first. Photography as support, never as wallpaper. One accent
   colour. Hairlines instead of boxes. Thin icons without containers.
   ------------------------------------------------------------------ */

const ICON = { strokeWidth: 1.5, absoluteStrokeWidth: true, "aria-hidden": true };

/* Word-by-word reveal for the display heading. The second sentence is set in a quieter tone. */
const Words = ({ text, dim = false, offset = 0, reduce }) => (
  <>
    {text.split(" ").map((w, i) => (
      <span key={`${w}-${i}`}>
        <span className="word-clip">
          <motion.span
            className={cn("inline-block", dim && "text-foreground/40")}
            initial={reduce ? false : { y: "108%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.75, ease: EASE, delay: 0.05 * (offset + i) }}
          >
            {w}
          </motion.span>
        </span>{" "}
      </span>
    ))}
  </>
);

/* Photo inside a tile. Three quiet layers of motion, all transform-only:
   1) a barely-there "settle" from 1.025 → 1 when the hero appears (once),
   2) scroll parallax (y) and pointer parallax (x/y) on the wrapper,
   3) a long, soft zoom on hover (CSS, .hero-tile). Nothing is written on top of the photo. */
const ParallaxPhoto = ({ src, alt, x, y, inset = "inset-[-12%]", priority = false, sizes, delay = 0.5, reduce = false }) => (
  <motion.div style={{ x, y }} className={cn("absolute", inset)}>
    <motion.div className="h-full w-full" initial={reduce ? false : { scale: 1.025 }} animate={{ scale: 1 }} transition={{ duration: 3.2, ease: [0.16, 1, 0.3, 1], delay }}>
      <Img src={src} alt={alt} sizes={sizes} loading={priority ? "eager" : "lazy"} fetchPriority={priority ? "high" : undefined} className="img-premium hero-img h-full w-full object-cover" />
    </motion.div>
  </motion.div>
);

const timeOf = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${String(d.getHours()).padStart(2, "0")}.${String(d.getMinutes()).padStart(2, "0")}`;
};

/* Live numbers from the operations system. Only rendered when the API answers. */
export const LivePulse = ({ pulse, testid = "public-live-strip" }) => {
  if (!pulse) return null;
  const places = (pulse.places || []).slice(0, 4);
  const t = timeOf(pulse.updated_at);
  return (
    <div className="flex flex-col justify-between rounded-2xl bg-ink p-6 text-white sm:p-7" data-testid={testid} aria-live="polite">
      <div className="flex items-center justify-between gap-4 text-[12.5px] font-medium">
        <span className="inline-flex items-center gap-2 text-white/80">
          <span className="live-dot inline-block h-2 w-2 shrink-0 rounded-full bg-dot" aria-hidden />
          {PULSE.label}
        </span>
        {t && <span className="tabular text-white/50" data-testid={`${testid}-time`}>{PULSE.updated} {t}</span>}
      </div>
      <div className="mt-5 flex flex-wrap items-end gap-x-8 gap-y-3">
        <div>
          <p className="t-num text-[2.25rem] leading-none sm:text-[2.5rem]" data-testid={`${testid}-montorar`}>{pulse.montorar_ute}</p>
          <p className="mt-1.5 text-[13.5px] text-white/60">{PULSE.montorar}</p>
        </div>
        <div>
          <p className="t-num text-[2.25rem] leading-none sm:text-[2.5rem]" data-testid={`${testid}-orders`}>{pulse.active_orders}</p>
          <p className="mt-1.5 text-[13.5px] text-white/60">{PULSE.orders}</p>
        </div>
        {places.length > 0 && (
          <p className="ml-auto self-end text-[13.5px] text-white/60" data-testid={`${testid}-places`}>{PULSE.places} {places.join(" · ")}</p>
        )}
      </div>
    </div>
  );
};

export const HomeHero = ({ title, lead, sub, pulse, actions, testid = "public-hero" }) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yMain = useTransform(scrollYProgress, [0, 1], [18, -46]);
  const ySide = useTransform(scrollYProgress, [0, 1], [26, -44]);

  /* Pointer parallax (desktop, fine pointer only): the photos drift a few pixels against the cursor. */
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const smx = useSpring(mx, { stiffness: 50, damping: 18, mass: 0.6 });
  const smy = useSpring(my, { stiffness: 50, damping: 18, mass: 0.6 });
  const xMain = useTransform(smx, (v) => v * -3);
  const yMainAll = useTransform([yMain, smy], ([a, b]) => a + b * -2);
  const xSide = useTransform(smx, (v) => v * 4);
  const ySideAll = useTransform([ySide, smy], ([a, b]) => a + b * 3);
  const fine = typeof window !== "undefined" && window.matchMedia?.("(hover: hover) and (pointer: fine)")?.matches;
  const onMove = (e) => {
    if (reduce || !fine || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 2);
    my.set(((e.clientY - r.top) / r.height - 0.5) * 2);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  const [firstRaw, ...rest] = title.split(". ");
  const first = rest.length ? `${firstRaw}.` : firstRaw;
  const second = rest.join(". ");

  const fade = (delay) => ({
    initial: reduce ? false : { opacity: 0, y: 14 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: EASE, delay },
  });

  return (
    <section className="container-public pt-12 sm:pt-16 lg:pt-20" data-testid={testid}>
      <h1 className="t-hero" data-testid={`${testid}-title`}>
        <span className="block"><Words text={first} reduce={reduce} /></span>
        {second && <span className="block"><Words text={second} dim offset={first.split(" ").length} reduce={reduce} /></span>}
      </h1>

      <motion.div className="mt-10 grid gap-6 lg:grid-cols-12 lg:items-end lg:gap-10" {...fade(0.35)}>
        <div className="max-w-2xl lg:col-span-7">
          <p className="t-lead">{lead}</p>
          {sub && <p className="mt-4 text-[17px] leading-relaxed text-muted-foreground">{sub}</p>}
        </div>
        <div className="flex flex-col gap-4 lg:col-span-5 lg:items-end">
          <div className="flex flex-wrap items-center gap-3">{actions}</div>
          <p className="text-[13.5px] text-muted-foreground">
            <a href={COMPANY.phoneHref} className="u-link tabular font-medium text-foreground" data-testid={`${testid}-phone-link`}>{COMPANY.phone}</a>
            {" · "}
            <a href={`mailto:${COMPANY.email}`} className="u-link font-medium text-foreground" data-testid={`${testid}-email-link`}>{COMPANY.email}</a>
          </p>
        </div>
      </motion.div>

      {/* Mosaic: one large photo on the left; a photo, a tile in the brand colour and the live tile on the right. */}
      <motion.div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} className="mt-12 grid gap-4 sm:mt-16 lg:grid-cols-12 lg:gap-5" {...fade(0.5)} data-testid={`${testid}-mosaic`}>
        <div className="hero-tile relative aspect-[4/3] overflow-hidden rounded-2xl bg-muted sm:aspect-[3/2] lg:col-span-8 lg:aspect-auto lg:min-h-[780px]">
          <ParallaxPhoto src={IMG.home_hero} alt={HERO.alt.home} x={reduce ? 0 : xMain} y={reduce ? 0 : yMainAll} priority sizes="(min-width: 1024px) 60vw, 100vw" delay={0.45} reduce={reduce} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:col-span-4 lg:grid-cols-1 lg:grid-rows-[minmax(320px,1fr)_auto_auto] lg:gap-5">
          <div className="hero-tile relative aspect-[4/3] overflow-hidden rounded-2xl bg-muted lg:aspect-auto">
            <ParallaxPhoto src={IMG.home_ev} alt={HERO.alt.ev} x={reduce ? 0 : xSide} y={reduce ? 0 : ySideAll} inset="inset-[-18%]" priority sizes="(min-width: 1024px) 32vw, (min-width: 640px) 50vw, 100vw" delay={0.6} reduce={reduce} />
          </div>
          <div className="flex flex-col rounded-2xl bg-brand p-6 text-on-brand sm:p-7" data-testid={`${testid}-fact-tile`}>
            {HERO_TILE.label && <p className="mb-3 text-[12.5px] font-medium text-[hsl(var(--on-brand)/0.7)]">{HERO_TILE.label}</p>}
            <h2 className="font-display text-[1.55rem] font-medium leading-[1.08] tracking-[-0.025em] sm:text-[1.7rem]">{HERO_TILE.title}</h2>
            <p className="mt-3 max-w-[36ch] text-[14.5px] leading-relaxed text-[hsl(var(--on-brand)/0.8)]">{HERO_TILE.text}</p>
            <dl className="mt-6 grid grid-cols-3 gap-4 border-t border-[hsl(var(--on-brand)/0.2)] pt-5">
              {HERO_TILE.facts.map(([v, l]) => (
                <div key={l} className="min-w-0">
                  <dd className="font-display text-[1.05rem] font-semibold leading-tight tracking-[-0.02em] sm:text-[1.15rem]">{v}</dd>
                  <dt className="mt-1 text-[12px] leading-snug text-[hsl(var(--on-brand)/0.7)]">{l}</dt>
                </div>
              ))}
            </dl>
          </div>
          <div className="sm:col-span-2 lg:col-span-1">
            <LivePulse pulse={pulse} />
          </div>
        </div>
      </motion.div>
    </section>
  );
};

/* ------------------------------------------------------------------
   Chapter head. Index, label, title on the left; a short text and an
   action on the right. Shared by every section below the hero.
   ------------------------------------------------------------------ */
const Chapter = ({ index, label, title, text, action, dark = false, className = "", titleClass = "" }) => (
  <div className={cn("grid gap-6 lg:grid-cols-12 lg:items-end lg:gap-10", className)}>
    <Reveal className="lg:col-span-7">
      <p className={cn("flex items-center gap-3 text-[13px] font-medium", dark ? "text-white/55" : "text-muted-foreground")}>
        <span className="font-mono text-[12px] tracking-[0.04em]">{index}</span>
        <span className={cn("h-px w-5", dark ? "bg-white/30" : "bg-foreground/25")} aria-hidden />
        {label}
      </p>
      <h2 className={cn("t-h2 mt-4 max-w-[20ch]", dark && "text-white", titleClass)}>{title}</h2>
    </Reveal>
    {(text || action) && (
      <Reveal delay={0.06} className="lg:col-span-5 lg:pb-1.5">
        {text && <p className={cn("max-w-[44ch] text-[16px] leading-relaxed", dark ? "text-white/65" : "text-muted-foreground")}>{text}</p>}
        {action && <div className="mt-4">{action}</div>}
      </Reveal>
    )}
  </div>
);

/* ------------------------------------------------------------------
   Reference marquee. Moves on its own, and faster – or backwards – with
   the reader's scroll. Pauses on hover. Static when motion is reduced.
   ------------------------------------------------------------------ */
const MarqueeItem = ({ c, hidden = false }) => (
  <li className="flex items-center gap-10 font-display text-[1.45rem] font-medium tracking-[-0.025em] text-foreground/50 transition-colors duration-300 hover:text-foreground sm:gap-14 sm:text-[1.8rem]" aria-hidden={hidden || undefined}>
    <span className="whitespace-nowrap">{c}</span>
    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-dot" aria-hidden />
  </li>
);

export const Ticker = ({ items, label, testid = "public-clients" }) => {
  const reduce = useReducedMotion();
  const baseX = useMotionValue(0);
  const { scrollY } = useScroll();
  const velocity = useVelocity(scrollY);
  const smooth = useSpring(velocity, { damping: 50, stiffness: 400 });
  const factor = useTransform(smooth, [0, 1200], [0, 4], { clamp: false });
  const x = useTransform(baseX, (v) => `${wrap(-50, 0, v)}%`);
  const dir = useRef(1);
  const paused = useRef(false);

  useAnimationFrame((_, delta) => {
    if (reduce || paused.current) return;
    const f = factor.get();
    if (f < -0.05) dir.current = -1;
    else if (f > 0.05) dir.current = 1;
    const move = dir.current * 1.5 * (delta / 1000) * (1 + Math.min(Math.abs(f), 6));
    baseX.set(baseX.get() + move);
  });

  return (
    <section className="container-public mt-16 sm:mt-24" data-testid={testid} aria-label={label}>
      <div className="flex flex-col gap-5 border-y border-border py-7 md:flex-row md:items-center md:gap-10">
        <p className="shrink-0 text-[13px] font-medium text-muted-foreground md:w-40">{label}</p>
        {reduce ? (
          <ul className="flex flex-wrap gap-x-10 gap-y-3">{items.map((c) => <MarqueeItem key={c} c={c} />)}</ul>
        ) : (
          <div className="mask-fade-x min-w-0 flex-1 overflow-hidden" onMouseEnter={() => { paused.current = true; }} onMouseLeave={() => { paused.current = false; }}>
            <motion.ul style={{ x }} className="flex w-max items-center gap-10 sm:gap-14" data-testid={`${testid}-track`}>
              {items.map((c) => <MarqueeItem key={c} c={c} />)}
              {items.map((c) => <MarqueeItem key={`${c}-dup`} c={c} hidden />)}
            </motion.ul>
          </div>
        )}
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------
   Services. Four rows, one open at a time, driving a sticky photo on
   the left. Advances on its own while in view until the reader takes
   over; a thin progress bar shows the rhythm. Click a title to go.
   ------------------------------------------------------------------ */
const MARKET_ICONS = { industri: Factory, samferdsel: Route, "bygg-naering": Building2, "service-bustad": House };
const AUTO_MS = 5600;

/* True from the lg breakpoint up – the only place the showcase advances on its own. */
const useDesktop = () => {
  const [on, setOn] = useState(() => (typeof window !== "undefined" ? window.matchMedia("(min-width: 1024px)").matches : false));
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const fn = (e) => setOn(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  return on;
};

export const ServicesShowcase = ({ intro, testid = "public-markets" }) => {
  const reduce = useReducedMotion();
  const desktop = useDesktop();
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-20% 0px -20% 0px" });
  const [active, setActive] = useState(0);
  const [cycle, setCycle] = useState(0);
  const [hold, setHold] = useState(false);
  const playing = desktop && inView && !hold && !reduce;
  const current = MARKETS[MARKET_ORDER[active]];

  useEffect(() => {
    if (!playing) return undefined;
    const id = setTimeout(() => { setActive((a) => (a + 1) % MARKET_ORDER.length); setCycle((c) => c + 1); }, AUTO_MS);
    return () => clearTimeout(id);
  }, [playing, active, cycle]);

  const pick = (i) => { setActive(i); setCycle((c) => c + 1); };

  return (
    <section id="tenester" ref={ref} className="container-public scroll-mt-24 pt-24 sm:pt-32" data-testid={testid} data-playing={playing ? "true" : "false"}>
      <Chapter index="01" label={intro.label} title={intro.title} text={intro.text} />
      <div className="mt-12 grid gap-10 lg:mt-16 lg:grid-cols-12 lg:gap-14">
        {/* Sticky preview */}
        <div className="hidden lg:col-span-5 lg:block">
          <div className="sticky top-24" data-testid={`${testid}-preview`}>
            <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-muted">
              {MARKET_ORDER.map((slug, i) => {
                const on = active === i;
                return (
                  <motion.div
                    key={slug}
                    className="absolute inset-0"
                    initial={false}
                    animate={{ opacity: on ? 1 : 0, scale: on ? 1.07 : 1 }}
                    transition={{ opacity: { duration: 0.7, ease: EASE }, scale: on ? { duration: AUTO_MS / 1000 + 1.5, ease: "linear" } : { duration: 0 } }}
                    aria-hidden={!on}
                  >
                    <Img src={MARKETS[slug].photo || sized(MARKETS[slug].preview, 1200)} alt={MARKETS[slug].photoAlt || ""} sizes="(min-width: 1024px) 42vw, 100vw" loading={i === 0 ? "eager" : "lazy"} className="img-premium h-full w-full object-cover" style={{ objectPosition: MARKETS[slug].focus || "50% 50%" }} />
                  </motion.div>
                );
              })}
              <div className="absolute inset-x-5 bottom-5 flex items-center gap-1.5" aria-hidden>
                {MARKET_ORDER.map((slug, i) => (
                  <span key={slug} className="relative h-[3px] flex-1 overflow-hidden rounded-full bg-white/30">
                    {active === i ? (
                      <span key={cycle} className="svc-progress absolute inset-0 rounded-full bg-white" data-paused={playing ? "false" : "true"} style={{ "--dur": `${AUTO_MS}ms`, transform: playing ? undefined : "scaleX(1)" }} />
                    ) : (
                      i < active && <span className="absolute inset-0 rounded-full bg-white/70" />
                    )}
                  </span>
                ))}
              </div>
            </div>
            <Link to={`/${current.slug}`} className="link-arrow mt-5 flex items-baseline justify-between gap-4 text-[15px] font-medium text-foreground hover:text-brand" data-testid={`${testid}-preview-link`}>
              <span><span className="mr-3 font-mono text-[12.5px] text-muted-foreground">0{active + 1}</span>{current.title}</span>
              <span className="inline-flex items-center gap-1.5 text-muted-foreground">{current.cta} <ArrowRight className="h-4 w-4" strokeWidth={1.75} aria-hidden /></span>
            </Link>
          </div>
        </div>

        <ul className="border-t border-border lg:col-span-7" data-testid={`${testid}-list`} onMouseEnter={() => setHold(true)} onMouseLeave={() => setHold(false)} onFocusCapture={() => setHold(true)} onBlurCapture={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setHold(false); }}>
          {MARKET_ORDER.map((slug, i) => {
            const m = MARKETS[slug];
            const Icon = MARKET_ICONS[slug];
            const on = active === i;
            return (
              <li key={slug} className="relative border-b border-border" data-active={on ? "true" : "false"}>
                {on && <motion.span layoutId="svc-highlight" className="absolute -inset-x-4 inset-y-1 z-0 rounded-2xl bg-secondary/70 sm:-inset-x-6" transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 420, damping: 40, mass: 0.8 }} aria-hidden />}
                <Link
                  to={`/${slug}`}
                  onMouseEnter={() => pick(i)}
                  onFocus={() => pick(i)}
                  className="group relative z-[1] grid grid-cols-[2.25rem_1fr_auto] items-center gap-4 py-6 focus-visible:outline-none sm:grid-cols-[3.5rem_1fr_auto] sm:gap-6 sm:py-7"
                  data-testid={`public-market-card-${slug}`}
                  aria-current={on ? "true" : undefined}
                >
                  <span className={cn("inline-flex h-7 w-7 transition-colors duration-300", on ? "text-brand" : "text-foreground/50")}>
                    <Icon className="h-7 w-7" {...ICON} />
                  </span>
                  <span className="min-w-0">
                    <span className="flex items-baseline gap-3">
                      <span className="font-mono text-[12.5px] text-muted-foreground">0{i + 1}</span>
                      <h3 className={cn("font-display text-[1.55rem] font-medium leading-tight tracking-[-0.025em] transition-colors duration-300 sm:text-[2rem]", on ? "text-foreground" : "text-foreground/70")}>{m.title}</h3>
                    </span>
                    {m.tagline && <span className={cn("mt-1.5 block text-[15.5px] transition-colors duration-300", on ? "text-muted-foreground" : "text-muted-foreground/70")}>{m.tagline}</span>}
                  </span>
                  <span className={cn("inline-flex h-10 w-10 items-center justify-center rounded-full border transition-[background-color,border-color,color] duration-300", on ? "border-foreground bg-foreground text-background" : "border-border text-foreground/40")}>
                    <ArrowUpRight className="link-diag h-[18px] w-[18px]" {...ICON} />
                  </span>
                </Link>
                <AnimatePresence initial={false}>
                  {on && (
                    <motion.div
                      key="body"
                      initial={reduce ? false : { height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
                      transition={{ duration: 0.5, ease: EASE }}
                      className="relative z-[1] overflow-hidden"
                    >
                      <div className="grid gap-5 pb-8 sm:grid-cols-[3.5rem_1fr] sm:gap-6">
                        <span aria-hidden className="hidden sm:block" />
                        <div>
                          <p className="max-w-[58ch] text-[15.5px] leading-relaxed text-muted-foreground">{m.desc}</p>
                          <ul className="mt-5 grid gap-x-8 gap-y-2 text-[14px] font-medium text-foreground/80 sm:grid-cols-2">
                            {(m.highlights || m.services.map(([t]) => t)).map((t) => (
                              <li key={t} className="flex items-center gap-2.5">
                                <span className="h-1 w-1 shrink-0 rounded-full bg-dot" aria-hidden />
                                {t}
                              </li>
                            ))}
                          </ul>
                          <div className="mt-6 aspect-[3/2] overflow-hidden rounded-xl bg-muted lg:hidden">
                            <Img src={m.photo || sized(m.preview, 900)} alt={m.photoAlt || ""} sizes="100vw" loading="lazy" className="img-premium h-full w-full object-cover" style={{ objectPosition: m.focus || "50% 50%" }} />
                          </div>
                          <Link to={`/${slug}`} className="link-arrow mt-5 inline-flex items-center gap-1.5 text-[15px] font-medium text-foreground hover:text-brand lg:hidden">
                            {m.cta} <ArrowRight className="h-4 w-4" strokeWidth={1.75} aria-hidden />
                          </Link>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------
   Prices. A price list read like a menu: the intro stays put on the
   left, the four most common jobs run down the right with the price
   set large. The whole row is the action.
   ------------------------------------------------------------------ */
const PRICE_ICONS = { heimeladar: PlugZap, sikringsskap: CircuitBoard, "el-kontroll": ShieldCheck, servicebesok: Wrench };

export const PriceBoard = ({ intro, items, note, demoLabel, testid = "public-prices-section" }) => (
  <section className="relative mt-24 overflow-hidden bg-secondary/60 py-20 sm:mt-32 sm:py-28" data-testid={testid}>
    <div className="container-public grid gap-12 lg:grid-cols-12 lg:gap-16">
      <div className="lg:col-span-4">
        <div className="lg:sticky lg:top-28">
          <Reveal>
            <p className="flex items-center gap-3 text-[13px] font-medium text-muted-foreground">
              <span className="font-mono text-[12px] tracking-[0.04em]">02</span>
              <span className="h-px w-5 bg-foreground/25" aria-hidden />
              {intro.label}
              <span className="sr-only" data-testid="public-prices-demo-tag"> · {demoLabel}</span>
            </p>
            <h2 className="t-h2 mt-4 max-w-[14ch]">{intro.title}</h2>
            <p className="mt-5 max-w-[40ch] text-[16px] leading-relaxed text-muted-foreground">{intro.text}</p>
            <div className="mt-6"><TextLink to="/prisar" data-testid="public-prices-all-link">{intro.link}</TextLink></div>
            <p className="mt-10 max-w-[40ch] border-t border-border pt-5 text-[13.5px] leading-relaxed text-muted-foreground" data-testid="public-prices-note">{note}</p>
          </Reveal>
        </div>
      </div>
      <ul className="border-t border-border lg:col-span-8" data-testid="public-price-cards">
        {items.map((item, i) => {
          const Icon = PRICE_ICONS[item.id] || Wrench;
          return (
            <Reveal as="li" key={item.id} delay={i * 0.05} className="border-b border-border" data-testid={`price-row-${item.id}`}>
              <Link
                to={`/kontakt?teneste=${encodeURIComponent(item.id)}`}
                className="group -mx-4 grid grid-cols-1 gap-y-5 rounded-2xl px-4 py-7 transition-colors duration-200 hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:-mx-6 sm:grid-cols-[3rem_minmax(0,1fr)_auto] sm:items-center sm:gap-x-8 sm:px-6 sm:py-8"
                data-testid={`price-order-${item.id}`}
              >
                <Icon className="hidden h-7 w-7 text-foreground/55 transition-colors duration-300 group-hover:text-brand sm:block" {...ICON} />
                <span className="min-w-0">
                  <span className="block text-[17px] font-medium leading-snug sm:text-[19px]">{item.name}</span>
                  <span className="mt-1.5 block max-w-[46ch] text-[14.5px] leading-relaxed text-muted-foreground">{item.short || item.desc}</span>
                </span>
                <span className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4 sm:flex-col sm:items-end sm:justify-start">
                  <span className="sm:text-right">
                    <span className="t-num block text-[1.75rem] leading-none sm:text-[2.25rem]">{item.price}</span>
                    <span className="mt-2 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-muted-foreground">
                      <span className="h-1.5 w-1.5 rounded-full bg-dot" aria-hidden />
                      {item.label || item.unit}
                    </span>
                  </span>
                  <span className="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-full border border-border bg-background px-4 text-[13.5px] font-medium text-foreground transition-[background-color,border-color,color] duration-300 group-hover:border-brand group-hover:bg-brand group-hover:text-on-brand">
                    {item.cta || "Bestill"}
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" strokeWidth={2} aria-hidden />
                  </span>
                </span>
              </Link>
            </Reveal>
          );
        })}
      </ul>
    </div>
  </section>
);

/* ------------------------------------------------------------------
   Key numbers that count up once when scrolled into view.
   ------------------------------------------------------------------ */
const CountUp = ({ value }) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-15% 0px" });
  const match = String(value).match(/^(.*?)(\d[\d\s]*)(.*)$/);
  const target = match ? parseInt(match[2].replace(/\s/g, ""), 10) : null;
  const [n, setN] = useState(target !== null && !reduce && target < 1000 ? 0 : target);

  useEffect(() => {
    if (!inView || target === null || reduce || target >= 1000) return undefined;
    const controls = animate(0, target, { duration: 1.4, ease: EASE, onUpdate: (v) => setN(Math.round(v)) });
    return () => controls.stop();
  }, [inView, target, reduce]);

  if (!match) return <span ref={ref}>{value}</span>;
  return <span ref={ref}>{match[1]}{n}{match[3]}</span>;
};

export const FactsLive = ({ items = FACTS, className = "", ...rest }) => (
  <dl className={cn("grid grid-cols-2 gap-x-8 gap-y-10 lg:grid-cols-4", className)} {...rest}>
    {items.map(([value, label], i) => (
      <Reveal key={label} delay={i * 0.05} className="border-t border-border pt-5">
        <dd className="t-num text-[2.75rem] sm:text-[3.5rem]"><CountUp value={value} /></dd>
        <dt className="mt-2 max-w-[22ch] text-[15px] leading-snug text-muted-foreground">{label}</dt>
      </Reveal>
    ))}
  </dl>
);

/* ------------------------------------------------------------------
   About. One statement that reads itself in as the page scrolls, word
   by word; then the facts and one wide photograph with a slow parallax.
   ------------------------------------------------------------------ */
const Word = ({ children, progress, range }) => {
  const opacity = useTransform(progress, range, [0.16, 1]);
  return <motion.span style={{ opacity }} className="inline-block">{children}&nbsp;</motion.span>;
};

const ScrollWords = ({ text, className = "" }) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.92", "end 0.55"] });
  const words = useMemo(() => text.split(" "), [text]);
  if (reduce) return <p ref={ref} className={className}>{text}</p>;
  return (
    <p ref={ref} className={className} aria-label={text}>
      {words.map((w, i) => <Word key={`${w}-${i}`} progress={scrollYProgress} range={[i / words.length, (i + 1) / words.length]}>{w}</Word>)}
    </p>
  );
};

export const Statement = ({ about, facts, image, imageAlt = "", testid = "public-statement" }) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-40, 40]);
  const [first, ...rest] = about.paragraphs;
  return (
    <section className="container-public pt-24 sm:pt-32" data-testid={testid}>
      <div className="grid gap-10 lg:grid-cols-12 lg:gap-16">
        <Reveal className="lg:col-span-4">
          <p className="flex items-center gap-3 text-[13px] font-medium text-muted-foreground">
            <span className="font-mono text-[12px] tracking-[0.04em]">03</span>
            <span className="h-px w-5 bg-foreground/25" aria-hidden />
            {about.label}
          </p>
          <h2 className="t-h2 mt-4 max-w-[14ch]">{about.title}</h2>
        </Reveal>
        <div className="lg:col-span-8">
          <ScrollWords text={first} className="t-statement max-w-[26ch] text-[clamp(1.6rem,2.9vw,2.75rem)]" />
          <div className="mt-8 grid gap-4 sm:grid-cols-2 sm:gap-8">
            {rest.map((t) => <p key={t} className="text-[16px] leading-relaxed text-muted-foreground">{t}</p>)}
          </div>
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
            <TextLink to={about.links[0][0]} data-testid={`${testid}-about-link`}>{about.links[0][1]}</TextLink>
            <TextLink to={about.links[1][0]} data-testid={`${testid}-careers-link`}>{about.links[1][1]}</TextLink>
          </div>
        </div>
      </div>

      <Reveal className="mt-14 sm:mt-20">
        <figure ref={ref}>
          <div className="relative aspect-[4/3] overflow-hidden rounded-2xl bg-muted sm:aspect-[21/9]">
            <motion.div style={{ y: reduce ? 0 : y }} className="absolute inset-[-8%]">
              <Img src={image} alt={imageAlt} sizes="100vw" loading="lazy" className="img-premium h-full w-full object-cover" />
            </motion.div>
          </div>
          <figcaption className="mt-4 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 text-[13px] text-muted-foreground">
            <span className="inline-flex items-center gap-2"><MapPin className="h-3.5 w-3.5" {...ICON} /> Kvinnherad, Vestland</span>
            <span>Hovudkontor i Rosendal · avdelingar på Husnes og Halsnøy</span>
          </figcaption>
        </figure>
      </Reveal>
    </section>
  );
};

/* ------------------------------------------------------------------
   Projects. One case set wide, then three set side by side. Photos
   drift gently on hover; the caption carries market, place and year.
   ------------------------------------------------------------------ */
const CaseTile = ({ project, index, wide = false, priority = false }) => (
  <Link to={`/prosjekt/${project.slug}`} className="group block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 focus-visible:ring-offset-background rounded-2xl" data-testid="public-project-tile">
    <div className={cn("relative overflow-hidden rounded-2xl bg-muted", wide ? "aspect-[4/3] sm:aspect-[16/9] lg:aspect-[21/9]" : "aspect-[4/3] lg:aspect-[4/5]")}>
      <Img src={sized(project.hero_image, wide ? 1800 : 1000)} alt="" sizes={wide ? "100vw" : "(min-width: 1024px) 33vw, 100vw"} loading={priority ? "eager" : "lazy"} className="case-img img-premium h-full w-full object-cover" />
      <span className="absolute left-4 top-4 inline-flex h-7 items-center rounded-full bg-background/85 px-2.5 font-mono text-[11.5px] text-foreground backdrop-blur-md" aria-hidden>0{index + 1}</span>
    </div>
    <div className={cn("mt-5 grid gap-3", wide ? "lg:grid-cols-12 lg:gap-10" : "")}>
      <div className={cn("min-w-0", wide && "lg:col-span-7")}>
        <p className="text-[13px] text-muted-foreground">{MARKET[project.market]} · {project.location} · {project.year}</p>
        <h3 className={cn("mt-1.5 font-display font-medium tracking-[-0.025em] text-foreground", wide ? "text-[1.75rem] leading-[1.08] sm:text-[2.4rem]" : "text-[1.3rem] leading-snug")}>{project.title}</h3>
      </div>
      <div className={cn("flex items-start justify-between gap-4", wide && "lg:col-span-5 lg:pt-6")}>
        <p className={cn("text-[14.5px] leading-relaxed text-muted-foreground", wide ? "max-w-[46ch] text-[15.5px]" : "")}>{project.subtitle}</p>
        <span className="inline-flex h-10 shrink-0 items-center gap-2 rounded-full border border-border px-4 text-[13.5px] font-medium text-foreground/70 transition-[background-color,border-color,color] duration-300 group-hover:border-foreground group-hover:bg-foreground group-hover:text-background">
          <span className="hidden sm:inline">{PROJECTS_INTRO.tile}</span>
          <ArrowUpRight className="link-diag h-[18px] w-[18px]" {...ICON} />
        </span>
      </div>
    </div>
  </Link>
);

export const FeaturedProjects = ({ intro, projects, testid = "public-featured-projects" }) => (
  <section className="container-public pt-24 sm:pt-32" data-testid={testid}>
    <Chapter index="04" label={intro.label} title={intro.title} text={intro.text} action={<TextLink to="/prosjekt" data-testid="public-projects-all-link">{intro.link}</TextLink>} />
    <div className="mt-12 grid gap-x-6 gap-y-14 lg:mt-16 lg:grid-cols-12" data-testid={`${testid}-grid`}>
      {projects === null && [0, 1, 2, 3].map((i) => (
        <div key={i} className={i === 0 ? "lg:col-span-12" : "lg:col-span-4"}>
          <Skeleton className={cn("w-full rounded-2xl", i === 0 ? "aspect-[21/9]" : "aspect-[4/5]")} />
          <Skeleton className="mt-5 h-4 w-1/3" />
          <Skeleton className="mt-3 h-7 w-2/3" />
        </div>
      ))}
      {projects && projects.length === 0 && (
        <p className="text-muted-foreground lg:col-span-12" data-testid={`${testid}-empty`}>Ingen prosjekt å vise akkurat no.</p>
      )}
      {projects?.slice(0, 4).map((p, i) => (
        <Reveal key={p.slug} delay={i === 0 ? 0 : ((i - 1) % 3) * 0.07} className={i === 0 ? "lg:col-span-12" : "sm:col-span-1 lg:col-span-4"}>
          <CaseTile project={p} index={i} wide={i === 0} priority={i === 0} />
        </Reveal>
      ))}
    </div>
  </section>
);

/* ------------------------------------------------------------------
   How we work. Dark band with a portrait that drifts on scroll, four
   points, and the three places on one line.
   ------------------------------------------------------------------ */
const WHY_ICONS = [Users, ClipboardCheck, FileCheck2, MapPin];
const PLACES = [
  { name: "Rosendal", role: "Hovudkontor", line: "Vangsvegen 13" },
  { name: "Husnes", role: "Avdeling" },
  { name: "Halsnøy", role: "Avdeling" },
];

export const WorkPoints = ({ label, title, items, image, imageAlt = "", testid = "public-why" }) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-30, 30]);
  return (
    <section ref={ref} className="grain relative mt-24 overflow-hidden bg-ink py-20 text-white sm:mt-32 sm:py-28" data-testid={testid}>
      <div className="container-public">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <Reveal className="lg:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-white/5">
              <motion.div style={{ y: reduce ? 0 : y }} className="absolute inset-[-6%]">
                <Img src={image} alt={imageAlt} sizes="(min-width: 1024px) 40vw, 100vw" loading="lazy" className="img-premium h-full w-full object-cover" />
              </motion.div>
            </div>
          </Reveal>
          <div className="flex flex-col justify-center lg:col-span-7">
            <Reveal>
              <p className="flex items-center gap-3 text-[13px] font-medium text-white/55">
                <span className="font-mono text-[12px] tracking-[0.04em]">05</span>
                <span className="h-px w-5 bg-white/30" aria-hidden />
                {label}
              </p>
              <h2 className="t-h2 mt-4 max-w-xl text-white">{title}</h2>
            </Reveal>
            <ul className="mt-12 grid gap-x-10 gap-y-10 sm:grid-cols-2">
              {items.map(([t, d], i) => {
                const Icon = WHY_ICONS[i % WHY_ICONS.length];
                return (
                  <Reveal as="li" key={t} delay={i * 0.06} className="border-t border-white/15 pt-5">
                    <div className="flex items-center justify-between">
                      <Icon className="h-6 w-6 text-brand" {...ICON} />
                      <span className="font-mono text-[12px] text-white/35">0{i + 1}</span>
                    </div>
                    <h3 className="mt-5 text-[17px] font-medium leading-snug">{t}</h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-white/60">{d}</p>
                  </Reveal>
                );
              })}
            </ul>
          </div>
        </div>

        {/* The three places */}
        <Reveal className="mt-16 sm:mt-20" data-testid={`${testid}-places`}>
          <ol className="relative grid gap-8 border-t border-white/15 pt-8 sm:grid-cols-3 sm:gap-6">
            {PLACES.map((p, i) => (
              <li key={p.name} className="flex items-start gap-4">
                <span className="relative mt-[7px] flex h-2.5 w-2.5 shrink-0">
                  <span className={cn("absolute inline-flex h-full w-full rounded-full bg-brand", i === 0 && "live-dot")} aria-hidden />
                </span>
                <div>
                  <p className="font-display text-[1.35rem] font-medium leading-tight tracking-[-0.02em]">{p.name}</p>
                  <p className="mt-1 text-[13.5px] text-white/55">{p.role}{p.line ? ` · ${p.line}` : ""}</p>
                </div>
              </li>
            ))}
          </ol>
        </Reveal>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------
   Process. Four steps on one line. The line draws itself and the
   markers light up as the reader scrolls through; the step that is
   "current" comes forward.
   ------------------------------------------------------------------ */
const STEP_ICONS = [Send, PhoneCall, Wrench, FileText];

const StepItem = ({ i, n, t, d, progress }) => {
  const Icon = STEP_ICONS[i % STEP_ICONS.length];
  const at = (i + 0.5) / n;
  const lit = useTransform(progress, [at - 0.12, at], [0, 1]);
  const numOpacity = useTransform(lit, [0, 1], [0.18, 1]);
  const dotScale = useTransform(lit, [0, 1], [0.6, 1]);
  return (
    <li className="relative pl-8 lg:pl-0 lg:pt-8">
      {/* marker on the line (vertical on small screens, horizontal on large) */}
      <motion.span style={{ opacity: lit, scale: dotScale }} className="absolute left-[-3.5px] top-[7px] h-[9px] w-[9px] rounded-full bg-brand lg:left-0 lg:top-[-4px]" aria-hidden />
      <span className="absolute left-[-3.5px] top-[7px] h-[9px] w-[9px] rounded-full border border-border bg-background lg:left-0 lg:top-[-4px] -z-10" aria-hidden />
      <div className="flex items-center justify-between">
        <motion.span style={{ opacity: numOpacity }} className="t-num text-[2.5rem] leading-none text-foreground sm:text-5xl">0{i + 1}</motion.span>
        <Icon className="h-6 w-6 text-foreground/55" {...ICON} />
      </div>
      <h3 className="mt-7 text-[19px] font-medium leading-snug">{t}</h3>
      <p className="mt-2 max-w-[34ch] text-[15px] leading-relaxed text-muted-foreground">{d}</p>
    </li>
  );
};

export const ProcessSteps = ({ label, title, text, steps, cta, testid = "public-process" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.85", "end 0.55"] });
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 26, mass: 0.5 });
  return (
    <section className="container-public pt-24 sm:pt-32" data-testid={testid}>
      <Chapter index="06" label={label} title={title} text={text} />
      <ol ref={ref} className="relative mt-14 grid gap-y-12 lg:mt-20 lg:grid-cols-4 lg:gap-x-8" data-testid={`${testid}-steps`}>
        {/* track + drawn line: vertical on small screens, horizontal on large */}
        <span className="absolute bottom-2 left-0 top-2 w-px bg-border lg:hidden" aria-hidden />
        <motion.span style={{ scaleY: progress }} className="absolute bottom-2 left-0 top-2 w-px origin-top bg-brand lg:hidden" aria-hidden />
        <span className="absolute inset-x-0 top-0 hidden h-px bg-border lg:block" aria-hidden />
        <motion.span style={{ scaleX: progress }} className="absolute inset-x-0 top-0 hidden h-px origin-left bg-brand lg:block" aria-hidden />
        {steps.map(([t, d], i) => <StepItem key={t} i={i} n={steps.length} t={t} d={d} progress={progress} />)}
      </ol>
      {cta && (
        <Reveal className="mt-14 flex flex-wrap items-center gap-5 lg:mt-16">
          <Btn to="/kontakt" size="lg" arrow data-testid={`${testid}-bestill-button`}>{cta}</Btn>
          <p className="text-[14px] text-muted-foreground">Du kan òg ringje oss på <a href={COMPANY.phoneHref} className="u-link tabular font-medium text-foreground">{COMPANY.phone}</a>.</p>
        </Reveal>
      )}
    </section>
  );
};

/* ------------------------------------------------------------------
   Closing contact band. The phone number is the hero.
   ------------------------------------------------------------------ */
export const HomeCTA = ({ cta, company = COMPANY, testid = "public-cta-section" }) => (
  <section className="container-public pt-24 sm:pt-32" data-testid={testid}>
    <Reveal>
      <div className="grain glow-brand relative overflow-hidden rounded-3xl bg-ink px-6 py-12 text-white sm:px-10 sm:py-16 lg:px-14 lg:py-20">
        <div className="relative grid gap-12 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-6">
            <p className="flex items-center gap-3 text-[13px] font-medium text-white/55">
              <span className="font-mono text-[12px] tracking-[0.04em]">07</span>
              <span className="h-px w-5 bg-white/30" aria-hidden />
              {cta.label}
            </p>
            <h2 className="t-h2 mt-4 max-w-[16ch] text-white">{cta.title}</h2>
            <p className="mt-5 max-w-lg text-[17px] leading-relaxed text-white/65">{cta.text}</p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Btn to="/kontakt" size="lg" arrow data-testid="public-cta-bestill-button">{cta.button || "Send førespurnad"}</Btn>
              <Btn href={company.phoneHref} variant="outlineLight" size="lg" data-testid="public-cta-phone-link">Ring {company.phone}</Btn>
            </div>
          </div>
          <div className="flex flex-col justify-end lg:col-span-6 lg:items-end lg:text-right">
            <p className="text-[13px] font-medium text-white/50">Ring oss</p>
            <a href={company.phoneHref} className="t-num mt-2 block text-[2.75rem] leading-none text-white transition-colors duration-200 hover:text-brand sm:text-[4rem] lg:text-[4.5rem]" data-testid="public-cta-phone-big">{company.phone}</a>
            <a href={`mailto:${company.email}`} className="u-link mt-4 text-[17px] font-medium text-white/85">{company.email}</a>
            <dl className="mt-8 grid grid-cols-3 gap-x-6 gap-y-2 text-[14.5px] lg:text-right">
              <div>
                <dt className="text-white/45">Hovudkontor</dt>
                <dd className="mt-1 text-white/85">{company.hq.visit.map((l) => <span key={l} className="block">{l}</span>)}</dd>
              </div>
              <div>
                <dt className="text-white/45">Postadresse</dt>
                <dd className="mt-1 text-white/85">{company.hq.post.map((l) => <span key={l} className="block">{l}</span>)}</dd>
              </div>
              <div>
                <dt className="text-white/45">Avdelingar</dt>
                <dd className="mt-1 text-white/85">{company.branchesText}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </Reveal>
  </section>
);
